import React, { useState, useEffect } from "react";
import moment from "moment";
import { Button, DatePicker, Input, Select, Switch } from "../../../../GeneralComponents/ui";
import i18n from "../../../../../Utils/i18next";
import { getPopupContainer } from "../../../../../Utils/Global";
import uuid from "react-uuid";
import { showNotificationWithIcon } from "../../../../../Utils/Notification";

const { Option } = Select;

const DashboardVariableCreateUpdate = ({ variables, variable, onSave, onCancel, onDelete }) => {
    const [duplicateNameError, setDuplicateNameError] = useState(false);
    const [showTime, setShowTime] = useState(false);
    const [multiInputValue, setMultiInputValue] = useState("");
    const [dateCache, setDateCache] = useState({});
    const [tempRecord, setTempRecord] = useState({
        name: "",
        dataType: "text",
        inputType: "single",
        defaultValue: ""
    });

    useEffect(() => {
        if (variable) {
            setTempRecord({ inputType: "single", ...variable });

            if (variable.dataType === "date" && variable.defaultValue) {
                let hasTime = false;
                const initialCache = {};
                const checkVal = (v) => {
                    if (v && typeof v === "string" && v.includes(":")) {
                        hasTime = true;
                        initialCache[v.split(" ")[0]] = v;
                    }
                };

                if (Array.isArray(variable.defaultValue)) {
                    variable.defaultValue.forEach(checkVal);
                } else {
                    checkVal(variable.defaultValue);
                }

                setDateCache(initialCache);
                setShowTime(hasTime);
            } else {
                setDateCache({});
                setShowTime(false);
            }
        } else {
            setTempRecord({
                name: "",
                dataType: "text",
                inputType: "single",
                defaultValue: ""
            });
            setDateCache({});
            setShowTime(false);
        }
    }, [variable]);

    const handleNameChange = (value) => {
        const filteredValue = value.replace(/[^a-zA-Z0-9_]/g, "");
        const variableNames = variables.filter(v => v.id !== tempRecord.id).map(v => v.name);

        setDuplicateNameError(variableNames.includes(filteredValue));
        setTempRecord(prev => ({
            ...prev,
            "name": filteredValue
        }));
    }

    const handleDataTypeChange = (value) => {
        const record = { ...tempRecord };

        record.dataType = value;
        if (value === "text" && record.inputType === "range") {
            record.inputType = "single";
            if (Array.isArray(record.defaultValue)) {
                record.defaultValue = record.defaultValue[0] || "";
            }
        }

        if (value === "number") {
            const checkValidity = (val) => {
                if (typeof val === "number") {
                    return true
                } else if (!val) {
                    return false;
                } else {
                    return isNaN(Number(val));
                }
            }

            record.defaultValue = checkValidity(record.defaultValue) ? Number(record.defaultValue) : "";
        } else if (value === "date") {
            record.defaultValue = "";
            setShowTime(false);
        } else {
            record.defaultValue = record.defaultValue.toString();
        }

        setTempRecord(prev => ({
            ...prev,
            ...record
        }));
    }

    const handleDefaultValueChange = (value) => {
        if (tempRecord.dataType === "date") {
            const updateCache = (val) => {
                if (val && typeof val === "string" && val.includes(":")) {
                    setDateCache(prev => ({ ...prev, [val.split(" ")[0]]: val }));
                }
            };
            if (Array.isArray(value)) {
                value.forEach(updateCache);
            } else {
                updateCache(value);
            }
        }

        setTempRecord(prev => ({
            ...prev,
            "defaultValue": value
        }));
    }

    const handleRangeChange = (index, value) => {
        setTempRecord(prev => {
            const newRange = Array.isArray(prev.defaultValue) ? [...prev.defaultValue] : [undefined, undefined];
            newRange[index] = prev.dataType === "number" ? Number(value) : value;
            return { ...prev, defaultValue: newRange };
        });
    }

    const handleSave = () => {
        if (duplicateNameError) {
            showNotificationWithIcon(i18n.t("Error"), i18n.t("DashboardVariables.NameAlreadyExists"), "error");
            return;
        };

        const recordToSave = {
            ...tempRecord,
            id: tempRecord.id || uuid()
        };

        onSave(recordToSave);
    };

    const isDefaultValueEmpty = Array.isArray(tempRecord.defaultValue)
        ? tempRecord.defaultValue.length === 0 || tempRecord.defaultValue.every(v => v === "" || v === null || v === undefined)
        : (tempRecord.defaultValue === "" || tempRecord.defaultValue === null || tempRecord.defaultValue === undefined);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div>
                <p style={{ marginBottom: 8 }}>{i18n.t("DashboardVariables.Name")}</p>
                <Input
                    value={tempRecord.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder={i18n.t("DashboardVariables.Name")}
                />
            </div>
            <div>
                <p style={{ marginBottom: 8 }}>{i18n.t("DashboardVariables.DataType")}</p>
                <Select
                    style={{ width: "100%" }}
                    getPopupContainer={getPopupContainer}
                    value={tempRecord.dataType}
                    onChange={(value) => handleDataTypeChange(value)}
                >
                    <Option value="text">{i18n.t("DashboardVariables.DataTypes.Text")}</Option>
                    <Option value="number">{i18n.t("DashboardVariables.DataTypes.Number")}</Option>
                    <Option value="date">{i18n.t("DashboardVariables.DataTypes.Date")}</Option>
                </Select>
            </div>
            <div>
                <p style={{ marginBottom: 8 }}>{i18n.t("Dashboard.Configuration.Fields.DashboardVariableInputType")}</p>
                <Select
                    style={{ width: "100%" }}
                    getPopupContainer={getPopupContainer}
                    value={tempRecord.inputType}
                    onChange={(value) => setTempRecord(prev => {
                        let newDefault = prev.defaultValue;
                        if (value === "multiple" || value === "range") {
                            if (!Array.isArray(newDefault)) {
                                newDefault = newDefault ? [newDefault] : [];
                            }
                        } else {
                            if (Array.isArray(newDefault)) {
                                newDefault = newDefault[0] || "";
                            }
                        }
                        return { ...prev, inputType: value, defaultValue: newDefault };
                    })}
                >
                    <Option value="single">{i18n.t("Dashboard.Configuration.Fields.DashboardVariableSingleInput")}</Option>
                    <Option value="multiple">{i18n.t("Dashboard.Configuration.Fields.DashboardVariableMultipleInput")}</Option>
                    <Option value="range" disabled={tempRecord.dataType === "text"}>{i18n.t("Dashboard.Configuration.Fields.DashboardVariableRangeInput")}</Option>
                </Select>
            </div>
            <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <p style={{ margin: 0 }}>{i18n.t("DashboardVariables.DefaultValue")}</p>
                    {tempRecord.dataType === "date" && (
                        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 12 }}>{i18n.t("DashboardVariables.ShowTime")}</span>
                            <Switch
                                size="small"
                                checked={showTime}
                                onChange={(checked) => {
                                    setShowTime(checked);
                                    setTempRecord(prev => {
                                        let newDefault = prev.defaultValue;
                                        if (!newDefault || (Array.isArray(newDefault) && newDefault.length === 0)) {
                                            return prev;
                                        }

                                        if (checked) {
                                            const restoreTime = (val) => {
                                                if (!val) return val;
                                                if (typeof val === "string" && val.includes(":")) return val;
                                                const dateOnly = val.split(" ")[0];
                                                return dateCache[dateOnly] || moment(val).format("YYYY-MM-DD 00:00:00");
                                            };
                                            if (Array.isArray(newDefault)) {
                                                newDefault = newDefault.map(restoreTime);
                                            } else {
                                                newDefault = restoreTime(newDefault);
                                            }
                                        } else {
                                            const newCache = { ...dateCache };
                                            const stripTime = (val) => {
                                                if (!val) return val;
                                                const dateOnly = moment(val).format("YYYY-MM-DD");
                                                if (typeof val === "string" && val.includes(":")) {
                                                    newCache[dateOnly] = val;
                                                }
                                                return dateOnly;
                                            };

                                            if (Array.isArray(newDefault)) {
                                                newDefault = newDefault.map(stripTime);
                                            } else {
                                                newDefault = stripTime(newDefault);
                                            }
                                            setDateCache(newCache);
                                        }

                                        return {
                                            ...prev,
                                            defaultValue: newDefault
                                        };
                                    });
                                }}
                            />
                        </span>
                    )}
                </div>

                {tempRecord.inputType === "single" && (
                    tempRecord.dataType === "date" ? (
                        <DatePicker
                            style={{ width: "100%" }}
                            showTime={showTime}
                            format={showTime ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD"}
                            getPopupContainer={getPopupContainer}
                            value={tempRecord.defaultValue ? moment(tempRecord.defaultValue) : null}
                            onChange={(_dateValue, dateString) => handleDefaultValueChange(dateString)}
                        />
                    ) : (
                        <Input
                            value={tempRecord.defaultValue}
                            onChange={(e) => handleDefaultValueChange(tempRecord.dataType === "number" ? Number(e.target.value) : e.target.value)}
                            placeholder={i18n.t("DashboardVariables.DefaultValue")}
                            type={tempRecord.dataType === "number" ? "number" : "text"}
                        />
                    )
                )}

                {tempRecord.inputType === "multiple" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
                        {Array.isArray(tempRecord.defaultValue) && tempRecord.defaultValue.length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                                {tempRecord.defaultValue.map((val, index) => (
                                    <div key={index} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "2px 8px", background: "#f0f0f0", border: "1px solid #d9d9d9", borderRadius: "4px", fontSize: "12px" }}>
                                        <span>{val}</span>
                                        <span style={{ cursor: "pointer", fontWeight: "bold" }} onClick={() => {
                                            const newVals = [...tempRecord.defaultValue];
                                            newVals.splice(index, 1);
                                            handleDefaultValueChange(newVals);
                                        }}>×</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {tempRecord.dataType === "date" ? (
                            <DatePicker
                                style={{ width: "100%" }}
                                showTime={showTime}
                                format={showTime ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD"}
                                getPopupContainer={getPopupContainer}
                                value={null}
                                onChange={(_dateValue, dateString) => {
                                    if (dateString) {
                                        const currentVals = Array.isArray(tempRecord.defaultValue) ? tempRecord.defaultValue : [];
                                        if (!currentVals.includes(dateString)) {
                                            handleDefaultValueChange([...currentVals, dateString]);
                                        }
                                    }
                                }}
                            />
                        ) : (
                            <Input
                                type={tempRecord.dataType === "number" ? "number" : "text"}
                                placeholder={i18n.t("DashboardVariables.DefaultValue")}
                                value={multiInputValue}
                                onChange={(e) => setMultiInputValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        if (multiInputValue !== "") {
                                            const parsedVal = tempRecord.dataType === "number" ? Number(multiInputValue) : multiInputValue;
                                            const currentVals = Array.isArray(tempRecord.defaultValue) ? tempRecord.defaultValue : [];
                                            if (!currentVals.includes(parsedVal)) {
                                                handleDefaultValueChange([...currentVals, parsedVal]);
                                            }
                                            setMultiInputValue("");
                                        }
                                    }
                                }}
                            />
                        )}
                    </div>
                )}

                {tempRecord.inputType === "range" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "4px 0" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            {tempRecord.dataType === "date" ? (
                                <DatePicker.RangePicker
                                    style={{ width: "100%" }}
                                    showTime={showTime}
                                    format={showTime ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD"}
                                    getPopupContainer={(trigger) => getPopupContainer(trigger, "body",)}
                                    value={Array.isArray(tempRecord.defaultValue) && tempRecord.defaultValue.length === 2 ? [tempRecord.defaultValue[0] ? moment(tempRecord.defaultValue[0]) : null, tempRecord.defaultValue[1] ? moment(tempRecord.defaultValue[1]) : null] : []}
                                    onChange={(_dates, dateStrings) => handleDefaultValueChange(dateStrings)}
                                />
                            ) : (
                                <>
                                    <Input
                                        type={tempRecord.dataType === "number" ? "number" : "text"}
                                        placeholder={i18n.t("Min")}
                                        value={Array.isArray(tempRecord.defaultValue) ? tempRecord.defaultValue[0] : ""}
                                        onChange={(e) => handleRangeChange(0, e.target.value)}
                                        style={{ flex: 1 }}
                                    />
                                    <span style={{ color: "#bfbfbf", fontWeight: 500 }}>-</span>
                                    <Input
                                        type={tempRecord.dataType === "number" ? "number" : "text"}
                                        placeholder={i18n.t("Max")}
                                        value={Array.isArray(tempRecord.defaultValue) ? tempRecord.defaultValue[1] : ""}
                                        onChange={(e) => handleRangeChange(1, e.target.value)}
                                        style={{ flex: 1 }}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <div style={{ display: "flex", justifyContents: "space-between", gap: "8px", marginTop: "16px" }}>
                {
                    variable && variable.id && onDelete &&
                    <Button danger onClick={() => onDelete(variable)}>{i18n.t("Delete")}</Button>
                }

                <div style={{display: "flex", justifyContent: "flex-end", gap: "8px", width: "100%"}}>
                    <Button onClick={onCancel}>{i18n.t("Cancel")}</Button>
                    <Button
                        type="primary"
                        onClick={handleSave}
                        disabled={!tempRecord.name || !tempRecord.dataType || isDefaultValueEmpty}
                    >{i18n.t("Save")}</Button>
                </div>
            </div>
        </div>
    );
};

export default DashboardVariableCreateUpdate;